import {
  autoPrintCompletedPosReceipt,
  loadLocalPrintProfile,
  localPrintNeedsPrinterCheck,
  printLocalSalesOrder,
  releaseLocalPrintIntent,
} from '@/lib/local-print'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import {
  claimDirectPrintJob,
  finishDirectPrintJob,
  getDirectPrintJobs,
  retryDirectPrintJob as retryServerDirectPrintJob,
} from '@/lib/services/snapdragon/snapdragon.api'

const activeJobs = new Map()
const DIRECT_PRINT_BATCH_SIZE = 20
const JOB_NOT_CLAIMED = Symbol('job-not-claimed')

function identityForProfile(organizationId, profile) {
  return {
    organizationId,
    workstationId: profile.workstationId,
    computerId: profile.computerId,
    machineId: profile.machineId,
  }
}

function attachDirectPrintRecovery(error, identity, job) {
  if (error && typeof error === 'object') {
    error.directPrintRecovery = {
      identity,
      jobId: job.id,
      salesOrderId: job.sales_order_id,
      clientJobId: job.client_job_id,
    }
  }
  return error
}

export function canRetryDirectPrintJob(error) {
  return Boolean(error?.directPrintRecovery?.jobId)
}

export async function retryDurableDirectPrintJob({ organizationId, job }) {
  const profile = loadLocalPrintProfile(organizationId)
  const workstationId = Number(job?.workstation_id ?? job?.workstationId)
  const jobId = job?.direct_print_job_id ?? job?.id
  const previousClientJobId = job?.client_job_id ?? job?.clientJobId

  if (!profile.token || !profile.workstationId || !profile.computerId) {
    throw new Error('Reconnect this browser to Snapdragon first.')
  }
  if (workstationId && Number(profile.workstationId) !== workstationId) {
    throw new Error('Retry this print from the computer where it was originally sent.')
  }
  if (!jobId) {
    throw new Error('This print job is no longer available to retry.')
  }
  if (!previousClientJobId) {
    throw new Error('Refresh print activity before retrying this job.')
  }
  if (
    !releaseLocalPrintIntent({
      intentOrganizationId: String(organizationId),
      intentKey: previousClientJobId,
    })
  ) {
    throw new Error(
      'This browser could not release the earlier print. Try again or contact support.'
    )
  }

  const identity = identityForProfile(organizationId, profile)
  const response = await retryServerDirectPrintJob({ ...identity, jobId })
  await processPendingDirectPrintJobs({
    organizationId,
    salesOrderId: job?.sales_order_id ?? snapdragonSalesOrderId(job),
  })
  return response
}

function snapdragonSalesOrderId(job) {
  const directSalesOrderId = Number(job?.sales_order_id)
  if (Number.isInteger(directSalesOrderId) && directSalesOrderId > 0) return directSalesOrderId

  const externalRef = String(job?.external_ref ?? '')
  const match = externalRef.match(/^sales-order:(\d+)$/)
  return match ? Number(match[1]) : undefined
}

export async function retryDirectPrintJob(error) {
  if (!canRetryDirectPrintJob(error)) return false

  const { identity, jobId, salesOrderId, clientJobId } = error.directPrintRecovery
  await retryDurableDirectPrintJob({
    organizationId: identity.organizationId,
    job: {
      direct_print_job_id: jobId,
      workstation_id: identity.workstationId,
      sales_order_id: salesOrderId,
      client_job_id: clientJobId,
    },
  })
  return true
}

async function printDirectJob(organizationId, job) {
  const settings = job.output_settings || {}
  if (job.output_kind === 'receipt') {
    return autoPrintCompletedPosReceipt({
      organizationId,
      salesOrderId: job.sales_order_id,
      clientJobId: job.client_job_id,
      outputOverride: settings,
    })
  }

  const kind = job.output_kind === 'super_ticket' ? 'superTicket' : 'orderSummary'
  return printLocalSalesOrder({
    organizationId,
    salesOrderId: job.sales_order_id,
    kind,
    source: 'auto',
    clientJobId: job.client_job_id,
    outputOverride: settings,
  })
}

async function processJob(identity, job) {
  const active = activeJobs.get(job.id)
  if (active) return active

  const run = (async () => {
    const claim = await claimDirectPrintJob({ ...identity, jobId: job.id })
    if (!claim?.claimed) return JOB_NOT_CLAIMED
    const claimAttempt = claim.job?.claim_attempts
    if (!Number.isInteger(claimAttempt) || claimAttempt < 1) {
      throw new Error('OneStem could not confirm ownership of this print job.')
    }

    let result
    try {
      result = await printDirectJob(identity.organizationId, claim.job || job)
      if (!result?.id) {
        throw Object.assign(new Error('Snapdragon did not accept this print job.'), {
          code: 'job_not_submitted',
        })
      }
    } catch (error) {
      const needsAttention = localPrintNeedsPrinterCheck(error)
      const failureRecorded = await finishDirectPrintJob({
        ...identity,
        jobId: job.id,
        claimAttempt,
        status: needsAttention
          ? 'needs_attention'
          : error?.code === 'job_not_submitted'
            ? 'pending'
            : 'failed',
        snapdragonJobId: error?.jobId,
        lastError: error?.message || 'Direct print failed.',
      }).then(
        () => true,
        () => false
      )
      throw needsAttention && failureRecorded
        ? attachDirectPrintRecovery(error, identity, job)
        : error
    }

    // If this acknowledgement fails, leave the row in processing. Recovery will
    // reuse the same client job ID, so Snapdragon can resolve it without a second copy.
    await finishDirectPrintJob({
      ...identity,
      jobId: job.id,
      claimAttempt,
      status: 'submitted',
      snapdragonJobId: result?.id,
    })
    return result
  })().finally(() => {
    if (activeJobs.get(job.id) === run) activeJobs.delete(job.id)
  })

  activeJobs.set(job.id, run)
  return run
}

export async function processPendingDirectPrintJobs({ organizationId, salesOrderId } = {}) {
  const profile = loadLocalPrintProfile(organizationId)
  if (!profile.token || !profile.workstationId || !profile.computerId) {
    return []
  }

  const identity = identityForProfile(organizationId, profile)
  const completed = []

  while (true) {
    const response = await getDirectPrintJobs({ ...identity, salesOrderId })
    const jobs = response?.jobs || []
    if (jobs.length === 0) return completed

    const results = await Promise.allSettled(jobs.map((job) => processJob(identity, job)))
    const failure = results.find((result) => result.status === 'rejected')
    if (failure) throw failure.reason

    const claimedResults = results
      .map((result) => result.value)
      .filter((result) => result !== JOB_NOT_CLAIMED)
    completed.push(...claimedResults)
    if (claimedResults.length === 0 || jobs.length < DIRECT_PRINT_BATCH_SIZE) return completed
  }
}
